import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table, Form, Row, Col, Button, Spinner, Badge, Alert,
  Pagination as BSPagination, OverlayTrigger, Tooltip, Modal
} from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
  postSeguimientoEquipos,
  postSeguimientoBulkTransicion,
} from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';
import { useAuth } from '../../hooks/useAuth';
import ValidarPermiso from '../Roles/ValidarPermiso';
import { ACCIONES_SEGUIMIENTO_EQUIPO } from '../Roles/ConstantRoute';
import { computarGrupoInfoMap } from '../../utils/gruposEquipos';

// Config por tab: estado filtrable + campo fecha + acción natural del tab.
// `accion` es la que preselecciona el banner de bulk; las tres acciones
// siguen disponibles en el select independientemente del tab actual.
const CONFIG_TAB = {
  Procesado: {
    label: 'Procesados',
    fechaField: 'fechaProcesado',
    fechaLabel: 'Fecha',
    accion: 'finalizar',
    accionLabel: 'Finalizar',
    icono: 'bi-cpu',
  },
  Finalizado: {
    label: 'Finalizados',
    fechaField: 'fechaCalibracion',
    fechaLabel: 'Fecha',
    accion: 'aprobar',
    accionLabel: 'Aprobar',
    icono: 'bi-check2-circle',
  },
  Aprobado: {
    label: 'Aprobados',
    fechaField: 'fechaAprobado',
    fechaLabel: 'Fecha',
    accion: 'entregar',
    accionLabel: 'Entregar',
    icono: 'bi-truck',
  },
};

// Acciones disponibles en el banner de selección masiva — siempre las tres,
// independiente del tab. El backend valida la transición válida por equipo.
const ACCIONES_BULK = [
  { value: 'finalizar', label: 'Finalizar', siguiente: 'Finalizado' },
  { value: 'aprobar',   label: 'Aprobar',   siguiente: 'Aprobado'   },
  { value: 'entregar',  label: 'Entregar',  siguiente: 'Entregado'  },
];

// Días transcurridos desde una fecha. Si la fecha específica no está
// registrada (equipos legacy a los que no se setea `fechaCalibracion` o
// `fechaAprobado` cuando el estado cambia), recorre los fallbacks hasta
// encontrar la primera fecha útil. Devuelve `{ dias, fecha, origen }`
// o `null` si no hay ninguna fecha disponible.
const resolverFecha = (eq, fechaField) => {
  const CANDIDATOS = {
    fechaCalibracion: ['fechaCalibracion', 'fechaProcesado', 'updatedAt', 'createdAt'],
    fechaAprobado:    ['fechaAprobado', 'fechaCalibracion', 'fechaProcesado', 'updatedAt', 'createdAt'],
    fechaProcesado:   ['fechaProcesado', 'fechaIngreso', 'updatedAt', 'createdAt'],
  };
  const campos = CANDIDATOS[fechaField] || [fechaField, 'updatedAt', 'createdAt'];
  for (const c of campos) {
    if (eq?.[c]) return { fecha: eq[c], origen: c };
  }
  return null;
};

const diasDesde = (fecha) => {
  if (!fecha) return null;
  return moment().startOf('day').diff(moment(fecha).startOf('day'), 'days');
};

// Semáforo por días: <8 verde, 8-10 naranja, >10 rojo.
const colorDias = (dias) => {
  if (dias > 10) return { bg: '#dc3545', color: '#fff' };
  if (dias >= 8) return { bg: '#fd7e14', color: '#fff' };
  return { bg: '#198754', color: '#fff' };
};

// Escapa HTML para inyectar texto proveniente del backend (motivos de
// fallidos) dentro del `html` de SweetAlert sin riesgo de XSS.
const escaparHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const PER_PAGE_OPTS = [10, 20, 50, 100];

const SeguimientoEquipos = ({ estado, activo = true, onCountersDirty, onNavegarAOt }) => {
  const config = CONFIG_TAB[estado];
  const { userData } = useAuth();
  const permisos = userData?.role?.permisos || [];
  const puedeAccion = ValidarPermiso(ACCIONES_SEGUIMIENTO_EQUIPO, permisos) !== -1
    || userData?.role?.nombre === 'Admin';

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    item: '',
    consecutivo: '',
    cliente: '',
    responsable: '',
    fechaDesde: '',
    fechaHasta: '',
  });
  const [filtersApplied, setFiltersApplied] = useState(filters);

  // Selección múltiple y banner de acción masiva.
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [accionBulk, setAccionBulk] = useState(config.accion);
  const [ejecutandoBulk, setEjecutandoBulk] = useState(false);
  const headerCheckRef = useRef(null);

  // Modal observación — reusa el mismo componente del listado principal de OT.
  const [modalObs, setModalObs] = useState(false);
  const [equipoObs, setEquipoObs] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await postSeguimientoEquipos(
        { estado, filters: filtersApplied },
        page,
        perPage
      );
      const payload = res?.data?.data || {};
      setResults(payload.results || []);
      setTotal(payload.total || 0);
      setTotalPages(payload.totalPages || 1);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudieron cargar los equipos');
    } finally {
      setLoading(false);
    }
  }, [estado, filtersApplied, page, perPage]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset page al cambiar filtros o tab.
  useEffect(() => { setPage(1); }, [filtersApplied, estado]);

  // Reset de selección al cambiar de página, perPage, filtros, tab o al
  // cambiar la pestaña activa (los tabs mantienen montaje: sin `activo`
  // la selección se arrastraría al volver a este tab).
  useEffect(() => {
    setSeleccionados(new Set());
  }, [page, perPage, filtersApplied, estado, activo]);

  const grupoInfoMap = useMemo(() => computarGrupoInfoMap(results), [results]);

  const todosMarcados = results.length > 0 && results.every(eq => seleccionados.has(eq._id));
  const algunosMarcados = seleccionados.size > 0 && !todosMarcados;

  useEffect(() => {
    if (headerCheckRef.current) headerCheckRef.current.indeterminate = algunosMarcados;
  }, [algunosMarcados]);

  const toggleOne = (id) => {
    setSeleccionados(prev => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
      return nuevo;
    });
  };

  const toggleAll = () => {
    setSeleccionados(todosMarcados ? new Set() : new Set(results.map(eq => eq._id)));
  };

  const clearAll = () => setSeleccionados(new Set());

  const handleFiltroChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const aplicarFiltros = () => {
    setPage(1);
    setFiltersApplied(filters);
  };

  const limpiarFiltros = () => {
    const vacio = { item: '', consecutivo: '', cliente: '', responsable: '', fechaDesde: '', fechaHasta: '' };
    setFilters(vacio);
    setFiltersApplied(vacio);
    setPage(1);
  };

  const ejecutarBulk = async () => {
    if (!puedeAccion || seleccionados.size === 0) return;
    const info = ACCIONES_BULK.find(a => a.value === accionBulk);
    const ids = Array.from(seleccionados);
    const confirm = await Swal.fire({
      title: `¿Aplicar "${info.label}" a ${ids.length} equipo(s)?`,
      html: `Pasarán al estado <b>${info.siguiente}</b>.<br/>
             <small class="text-muted">Esta transición no genera hoja de trabajo ni evidencias.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sí, ${info.label.toLowerCase()}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d6efd',
    });
    if (!confirm.isConfirmed) return;

    setEjecutandoBulk(true);
    try {
      const res = await postSeguimientoBulkTransicion(ids, accionBulk);
      const { exitosos = [], fallidos = [] } = res?.data?.data || {};
      if (fallidos.length === 0) {
        Alertassw('ok', `${exitosos.length} de ${ids.length} procesados`);
      } else {
        const lista = fallidos.slice(0, 3)
          .map(f => `<li>${escaparHtml(f.motivo)}</li>`).join('');
        const resto = fallidos.length > 3 ? `<li>y ${fallidos.length - 3} más</li>` : '';
        await Swal.fire({
          icon: exitosos.length > 0 ? 'warning' : 'error',
          title: `${exitosos.length} de ${ids.length} procesados`,
          html: `<ul class="text-start small">${lista}${resto}</ul>`,
        });
      }
      setSeleccionados(new Set());
      // Si la página actual quedó vacía tras el bulk, retrocede una página.
      if (exitosos.length === ids.length && results.length === ids.length && page > 1) {
        setPage(p => Math.max(1, p - 1));
      } else {
        await cargar();
      }
      if (onCountersDirty) onCountersDirty();
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudo ejecutar la acción en lote');
    } finally {
      setEjecutandoBulk(false);
    }
  };

  const abrirObs = (eq) => {
    // Adaptamos a la forma esperada por ObservacionOT (recibe `ordendata`).
    setEquipoObs(eq);
    setModalObs(true);
  };

  const renderPaginacion = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <BSPagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
          {n}
        </BSPagination.Item>
      );
    }
    return (
      <BSPagination size="sm" className="mb-0">
        <BSPagination.First disabled={page === 1} onClick={() => setPage(1)} />
        <BSPagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
        {items}
        <BSPagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
        <BSPagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
      </BSPagination>
    );
  };

  return (
    <div>
      {/* Modal de solo lectura con la observación del EquipmentInOt. No reusamos
          ObservacionOT porque ese actualiza el documento Ot, no el equipo. */}
      <Modal show={modalObs} onHide={() => setModalObs(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-chat-left-text me-2 text-primary" />
            Observación — {equipoObs?.consecutivo || 'Equipo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>{equipoObs?.snapshot?.item}</strong>
            <small className="text-muted d-block">
              {equipoObs?.snapshot?.marca} {equipoObs?.snapshot?.modelo} · SN: {equipoObs?.snapshot?.serial || '—'}
            </small>
          </div>
          <hr />
          <div style={{ whiteSpace: 'pre-wrap', minHeight: 60 }}>
            {equipoObs?.observacion || <span className="text-muted">Sin observación registrada.</span>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalObs(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* Header del tab */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className={`bi ${config.icono} me-2 text-primary`} />
          Equipos {config.label}
          <small className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
            — Acción disponible: <strong>{config.accionLabel}</strong>
          </small>
        </h5>
        <Badge bg="secondary">{total} equipo{total !== 1 ? 's' : ''}</Badge>
      </div>

      {!puedeAccion && (
        <Alert variant="info" className="py-2 small mb-3">
          <i className="bi bi-info-circle me-1" />
          Tu rol no tiene el permiso <strong>ACCIONES_SEGUIMIENTO_EQUIPO</strong>.
          Puedes ver los equipos pero no ejecutar la acción <strong>{config.accionLabel}</strong>.
        </Alert>
      )}

      {/* Banner de acción masiva — solo visible si hay permiso y selección. */}
      {puedeAccion && seleccionados.size > 0 && (
        <Alert
          variant="primary"
          className="py-2 mb-3 d-flex flex-wrap align-items-center gap-2"
          style={{ position: 'sticky', top: 0, zIndex: 10 }}
        >
          <strong>
            {seleccionados.size} equipo{seleccionados.size !== 1 ? 's' : ''} seleccionado{seleccionados.size !== 1 ? 's' : ''}
          </strong>
          <Form.Select
            size="sm"
            style={{ width: 160 }}
            value={accionBulk}
            onChange={e => setAccionBulk(e.target.value)}
            disabled={ejecutandoBulk}
          >
            {ACCIONES_BULK.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </Form.Select>
          <Button size="sm" variant="primary" onClick={ejecutarBulk} disabled={ejecutandoBulk}>
            {ejecutandoBulk ? <Spinner size="sm" animation="border" /> : 'Ejecutar'}
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={clearAll} disabled={ejecutandoBulk}>
            Deseleccionar todo
          </Button>
        </Alert>
      )}

      {/* Filtros */}
      <div className="bg-light border rounded p-2 mb-3">
        <Row className="g-2 align-items-end">
          <Col md={2}>
            <Form.Label className="small mb-1">Ítem</Form.Label>
            <Form.Control
              size="sm"
              value={filters.item}
              onChange={e => handleFiltroChange('item', e.target.value)}
              placeholder="Nombre del ítem..."
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Consecutivo</Form.Label>
            <Form.Control
              size="sm"
              value={filters.consecutivo}
              onChange={e => handleFiltroChange('consecutivo', e.target.value)}
              placeholder="Ej. 00123"
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Cliente</Form.Label>
            <Form.Control
              size="sm"
              value={filters.cliente}
              onChange={e => handleFiltroChange('cliente', e.target.value)}
              placeholder="Nombre del cliente..."
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Responsable</Form.Label>
            <Form.Control
              size="sm"
              value={filters.responsable}
              onChange={e => handleFiltroChange('responsable', e.target.value)}
              placeholder="Nombre o apellido..."
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">{config.fechaLabel} desde</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={filters.fechaDesde}
              onChange={e => handleFiltroChange('fechaDesde', e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">{config.fechaLabel} hasta</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={filters.fechaHasta}
              onChange={e => handleFiltroChange('fechaHasta', e.target.value)}
              disabled={!filters.fechaDesde}
              min={filters.fechaDesde || undefined}
            />
          </Col>
          <Col md={2} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={aplicarFiltros} disabled={loading}>
              <i className="bi bi-search me-1" />Filtrar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={limpiarFiltros} disabled={loading}>
              <i className="bi bi-x-circle" />
            </Button>
          </Col>
        </Row>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="text-muted mt-2 small">Cargando equipos...</div>
        </div>
      ) : results.length === 0 ? (
        <Alert variant="info" className="text-center py-4">
          <i className="bi bi-inbox fs-3 d-block mb-2" />
          No hay equipos {config.label.toLowerCase()} con los filtros aplicados.
        </Alert>
      ) : (
        // Scroll horizontal en pantallas pequeñas: `tableLayout: auto` + anchos
        // mínimos por columna hacen que las celdas no se encojan por debajo de
        // un tamaño legible. En móviles el usuario desplaza la tabla
        // lateralmente en vez de ver texto apilado o sobremontado.
        <div className="table-responsive">
          <Table
            bordered
            hover
            size="sm"
            style={{
              tableLayout: 'auto',
              fontSize: '0.78rem',
              width: '100%',
              minWidth: 1100,
            }}
          >
            <thead className="table-dark" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
              <tr>
                {puedeAccion && (
                  <th
                    className="text-center align-middle"
                    style={{ width: 40, minWidth: 40 }}
                  >
                    <div className="d-flex justify-content-center align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        ref={headerCheckRef}
                        checked={todosMarcados}
                        onChange={toggleAll}
                        disabled={ejecutandoBulk}
                        title="Seleccionar todos de esta página"
                        aria-label="Seleccionar todos de esta página"
                      />
                    </div>
                  </th>
                )}
                <th className="text-center align-middle" style={{ minWidth: 70 }}>Cons.</th>
                <th className="text-center align-middle" style={{ minWidth: 80 }}>OT</th>
                <th className="align-middle" style={{ minWidth: 150 }}>Cliente</th>
                <th className="align-middle" style={{ minWidth: 140 }}>Ítem</th>
                <th className="align-middle" style={{ minWidth: 150 }}>Equipo</th>
                <th className="align-middle" style={{ minWidth: 130 }}>Ubicación</th>
                <th className="align-middle" style={{ minWidth: 130 }}>Responsable</th>
                <th className="text-center align-middle" style={{ minWidth: 55 }}>Días</th>
                <th className="text-center align-middle" style={{ minWidth: 90 }}>{config.fechaLabel}</th>
                <th
                  className="text-center align-middle"
                  title="Obs"
                  style={{ minWidth: 40 }}
                >
                  <i className="bi bi-eye" />
                </th>
                <th
                  className="text-center align-middle"
                  title="Ingresado al laboratorio · Grupo padre/dependiente · Equipo adicionado"
                  style={{ minWidth: 60 }}
                >
                  <i className="bi bi-diagram-3" />
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map(eq => {
                const resuelto = resolverFecha(eq, config.fechaField);
                const fecha = resuelto?.fecha;
                const dias = diasDesde(fecha);
                const color = dias !== null ? colorDias(dias) : { bg: '#6c757d', color: '#fff' };
                const esFallback = resuelto && resuelto.origen !== config.fechaField;
                const grupoInfo = grupoInfoMap.get(String(eq._id));
                const estaIngresado = Boolean(eq.fechaIngreso);
                const cellStyle = { wordBreak: 'break-word', overflowWrap: 'anywhere' };
                // Fallback de cliente: EquipmentInOt.cliente puede ser null;
                // en ese caso usamos el cliente de la OT (otInfo.cliente),
                // que el backend adjunta.
                const nombreCliente =
                  eq.cliente?.nombre ||
                  eq.otInfo?.cliente?.nombre ||
                  null;
                const numeroOrden = eq.otInfo?.numeroOrden || eq.ot || null;
                const marcado = seleccionados.has(eq._id);
                return (
                  <tr key={eq._id} className={marcado ? 'table-active' : ''}>
                    {puedeAccion && (
                      <td className="align-middle" style={{ width: 40, minWidth: 40 }}>
                        <div className="d-flex justify-content-center align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={marcado}
                            onChange={() => toggleOne(eq._id)}
                            disabled={ejecutandoBulk}
                            aria-label={`Seleccionar equipo ${eq.consecutivo || eq._id}`}
                          />
                        </div>
                      </td>
                    )}
                    <td className="text-center align-middle fw-semibold" style={cellStyle}>
                      {eq.consecutivo || <span className="text-muted">—</span>}
                    </td>
                    <td className="text-center align-middle" style={cellStyle}>
                      {numeroOrden ? (
                        onNavegarAOt ? (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => onNavegarAOt(numeroOrden)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavegarAOt(numeroOrden); }}
                            title={`Ir al listado de OT filtrado por ${numeroOrden}`}
                            className="text-primary"
                            style={{
                              fontWeight: 600,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textUnderlineOffset: 2,
                            }}
                          >
                            {numeroOrden}
                          </span>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{numeroOrden}</span>
                        )
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="align-middle" style={cellStyle}>
                      {nombreCliente || <span className="text-muted">—</span>}
                    </td>
                    <td className="align-middle" style={cellStyle}>
                      {eq.snapshot?.item || <span className="text-muted">—</span>}
                    </td>
                    <td className="align-middle" style={cellStyle}>
                      <div><strong>{eq.snapshot?.marca || '—'}</strong></div>
                      <small className="text-muted d-block">{eq.snapshot?.modelo || ''}</small>
                      <small className="text-muted d-block">SN: {eq.snapshot?.serial || '—'}</small>
                    </td>
                    <td className="align-middle" style={cellStyle}>
                      <strong>{eq.snapshot?.sede || '—'}</strong>
                      <small className="text-muted d-block">{eq.snapshot?.ubicacion || ''}</small>
                    </td>
                    <td className="align-middle" style={cellStyle}>
                      {eq.responsable?.nombre
                        ? `${eq.responsable.nombre.charAt(0).toUpperCase()}. ${eq.responsable.apellido || ''}`
                        : <span className="text-muted">Sin asignar</span>}
                    </td>
                    <td className="text-center align-middle">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>
                          {fecha
                            ? `${moment(fecha).format('DD/MM/YYYY')}${esFallback ? ` (fuente: ${resuelto.origen})` : ''}`
                            : 'Sin fecha'}
                        </Tooltip>}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: color.bg,
                            color: color.color,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        >
                          {dias !== null ? dias : '—'}
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-center align-middle" style={{ fontSize: '0.75rem' }}>
                      {fecha ? (
                        <>
                          <div>{moment(fecha).format('DD/MM/YYYY')}</div>
                          {esFallback && (
                            <small className="text-muted d-block" style={{ fontSize: '0.6rem' }} title={`Campo original ${config.fechaField} vacío — usando ${resuelto.origen}`}>
                              <i className="bi bi-exclamation-circle me-1" />
                              {resuelto.origen}
                            </small>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center align-middle">
                      {eq.observacion ? (
                        <i
                          className="bi bi-eye-fill text-primary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => abrirObs(eq)}
                          title="Ver observación"
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center align-middle">
                      <div className="d-flex flex-column align-items-center gap-1">
                        {estaIngresado && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{eq.fechaSalida
                              ? `Salido del laboratorio ${moment(eq.fechaSalida).format('DD/MM/YYYY')}`
                              : `Ingresado al laboratorio ${moment(eq.fechaIngreso).format('DD/MM/YYYY')}`}</Tooltip>}
                          >
                            <i
                              className={`bi bi-geo-alt-fill ${eq.fechaSalida ? 'text-muted' : 'text-primary'}`}
                              style={{ fontSize: '0.8rem' }}
                            />
                          </OverlayTrigger>
                        )}
                        {grupoInfo && (
                          <span
                            style={{
                              fontSize: '0.55rem',
                              fontWeight: 700,
                              padding: '1px 3px',
                              lineHeight: 1.1,
                              borderRadius: 3,
                              background: grupoInfo.rol === 'padre' ? '#0d6efd' : '#0dcaf0',
                              color: grupoInfo.rol === 'padre' ? '#fff' : '#000',
                            }}
                            title={grupoInfo.rol === 'padre'
                              ? `Padre del grupo ${grupoInfo.grupoNum}`
                              : `Dependiente ${grupoInfo.grupoNum}${grupoInfo.letra || ''} del grupo ${grupoInfo.grupoNum}`}
                          >
                            {grupoInfo.rol === 'padre'
                              ? `P${grupoInfo.grupoNum}`
                              : `D${grupoInfo.grupoNum}${grupoInfo.letra || ''}`}
                          </span>
                        )}
                        {eq.adicion && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Equipo adicionado posteriormente</Tooltip>}
                          >
                            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '0.8rem' }} />
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Paginación */}
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Mostrar:</span>
              <Form.Select
                size="sm"
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                style={{ width: 80 }}
              >
                {PER_PAGE_OPTS.map(n => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
              <span className="small text-muted">
                {total > 0
                  ? `${(page - 1) * perPage + 1} - ${Math.min(page * perPage, total)} de ${total}`
                  : 'Sin resultados'}
              </span>
            </div>
            {renderPaginacion()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeguimientoEquipos;
